import React from 'react';
import {
  Menu,
  MenuItem,
  MenuItemLabel,
  Icon,
  ThreeDotsIcon,
  TrashIcon,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { useDialog } from '@/common/context/DialogContext';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';

interface Module {
  id: string;
  title: string;
}

interface ModuleMenuProps {
  module: Module;
  handleEditModule: () => void;
  pendingEvaluationsCount?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger: (props: any) => React.ReactElement;
}

export const ModuleMenu: React.FC<ModuleMenuProps> = ({
  module,
  handleEditModule,
  pendingEvaluationsCount,
  trigger,
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const { showDialog, hideDialog, showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const { deleteModuleMutation } = useLecturerModuleData(module.id, undefined, undefined);

  const handleDeleteModule = () => {
    showDialog({
      title: t('lecturer.modules.dialogs.delete.title'),
      message: t('lecturer.modules.dialogs.delete.message', { title: module.title }),
      buttons: [
        {
          text: t('common.buttons.cancel'),
          variant: 'outline',
          action: 'secondary',
          onPress: hideDialog,
        },
        {
          text: t('common.buttons.delete'),
          action: 'negative',
          onPress: async () => {
            try {
              await deleteModuleMutation.mutateAsync({ moduleId: module.id });
              showToastMessage('success', t('lecturer.modules.messages.deleteSuccess'));
              hideDialog();
            } catch (error: unknown) {
              reportApiError(error as Error, t('lecturer.modules.messages.deleteError'));
            }
          },
        },
      ],
    });
  };

  return (
    <Menu
      placement="right top"
      trigger={trigger}
    >
      <MenuItem key="edit" textValue="Edit" onPress={handleEditModule}>
        <MenuItemLabel size="sm">
          {t('common.buttons.edit')}
        </MenuItemLabel>
      </MenuItem>

      {pendingEvaluationsCount && pendingEvaluationsCount > 0 ? (
        <MenuItem
          key="evaluate"
          textValue="Evaluate"
          onPress={() => navigation.navigate('EvaluationDesk', { module })}
        >
          <MenuItemLabel size="sm">
            {t('lecturer.sidebar.actions.evaluate')}
          </MenuItemLabel>
        </MenuItem>
      ) : null}

      <MenuItem key="delete" textValue="Delete" onPress={handleDeleteModule}>
        <Icon as={TrashIcon} size="sm" mr="$2" color="$statusError" />
        <MenuItemLabel size="sm" color="$statusError">
          {t('common.buttons.delete')}
        </MenuItemLabel>
      </MenuItem>

      <MenuItem key="more" textValue="More">
        <Icon as={ThreeDotsIcon} size="sm" mr="$2" color="$textSubtle" />
        <MenuItemLabel size="sm">
          {t('common.buttons.more')}
        </MenuItemLabel>
      </MenuItem>
    </Menu>
  );
};
